import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiUploadedDatasetView } from '@euler/model/bi/dataset';
import { BiDatasetRef, BiDatasetRefKind } from '@euler/model/bi/query';
import { Database, FileSpreadsheet } from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';
import { BiChip } from '../../components/ui/BiChip';
import { BiRetry } from '../../components/ui/BiRetry';
import { EditorSection } from './EditorSection';
import { biSameDataset } from './functions/biEditorLabels';

/**
 * Which dataset a query is on: a built-in one, or an upload the caller may
 * use. Picking another clears the fields that named the old one's.
 */
export const DatasetSection = memo(
  ({
    title = '数据集',
    note = '换数据集会清空已选字段',
    dataset,
    builtin,
    uploaded,
    current,
    exclude,
    onPick,
    onRetryUploads,
  }: {
    title?: string;
    note?: string;
    dataset: BiDatasetRef | null;
    builtin: BiDatasetCatalog[];
    /** Null when the uploads could not be read. */
    uploaded: BiUploadedDatasetView[] | null;
    /** The picked dataset's catalog, to name an upload no longer listed. */
    current: BiDatasetCatalog | null;
    /** Not offered: the other side of a blend. */
    exclude?: BiDatasetRef | null;
    onPick: (dataset: BiDatasetRef) => void;
    onRetryUploads: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const chips = {
      flexDirection: 'row' as const,
      flexWrap: 'wrap' as const,
      gap: rpx(6),
    };
    const pick = (ref: BiDatasetRef) =>
      biSameDataset(dataset, ref) ? undefined : onPick(ref);
    const unlisted =
      dataset?.kind === BiDatasetRefKind.Upload &&
      !uploaded?.some(entry => entry.id === dataset.datasetId);
    return (
      <EditorSection title={title} note={note}>
        <View style={chips}>
          {builtin
            .filter(catalog => !biSameDataset(exclude ?? null, catalog.dataset))
            .map(catalog => (
              <BiChip
                key={JSON.stringify(catalog.dataset)}
                icon={Database}
                label={biText(catalog.label)}
                selected={biSameDataset(dataset, catalog.dataset)}
                onPress={() => pick(catalog.dataset)}
              />
            ))}
        </View>
        {uploaded?.length || unlisted ? (
          <>
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              上传的数据
            </Typography.Text>
            <View style={chips}>
              {(uploaded ?? [])
                .filter(
                  entry =>
                    !(
                      exclude?.kind === BiDatasetRefKind.Upload &&
                      exclude.datasetId === entry.id
                    ),
                )
                .map(entry => {
                  const ref: BiDatasetRef = {
                    kind: BiDatasetRefKind.Upload,
                    datasetId: entry.id,
                  };
                  return (
                    <BiChip
                      key={entry.id}
                      icon={FileSpreadsheet}
                      label={entry.name}
                      selected={biSameDataset(dataset, ref)}
                      onPress={() => pick(ref)}
                    />
                  );
                })}
              {unlisted ? (
                <BiChip
                  icon={FileSpreadsheet}
                  label={current ? biText(current.label) : '上传的数据'}
                  selected
                  onPress={() => undefined}
                />
              ) : null}
            </View>
          </>
        ) : null}
        {uploaded === null ? (
          <BiRetry
            label="上传的数据暂时读不到，点击重试"
            onRetry={onRetryUploads}
          />
        ) : null}
      </EditorSection>
    );
  },
);
